import React, { useState, useEffect, useRef } from 'react';
import { Company, PolicyType } from '../../types';
import { Search, X, FileText, ShieldCheck, ArrowRight, ExternalLink } from 'lucide-react';

interface QuickSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  companies: Company[];
  onSelect: (companyId: string, policyType: PolicyType) => void;
}

export const QuickSearchModal: React.FC<QuickSearchModalProps> = ({
  isOpen,
  onClose,
  companies,
  onSelect,
}) => {
  const [query, setQuery] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const normalizedQuery = query.toLowerCase().trim();

  const filteredCompanies = companies.filter((c) => {
    if (!normalizedQuery) return true;
    return (
      c.name.toLowerCase().includes(normalizedQuery) ||
      c.id.toLowerCase().includes(normalizedQuery) ||
      c.domain.toLowerCase().includes(normalizedQuery) ||
      (c.description && c.description.toLowerCase().includes(normalizedQuery)) ||
      (c.category && c.category.toLowerCase().includes(normalizedQuery))
    );
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-start justify-center pt-20 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="flex items-center px-4 py-3.5 border-b border-slate-200 dark:border-slate-700 gap-3">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search companies, domains, or policy types..."
            className="flex-1 bg-transparent border-none text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none text-sm"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline font-mono text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div className="overflow-y-auto p-3 space-y-2 divide-y divide-slate-100 dark:divide-slate-700/50">
          {filteredCompanies.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              No matching companies or policies found for "{query}"
            </div>
          ) : (
            filteredCompanies.map((c) => (
              <div key={c.id} className="pt-2 first:pt-0">
                <div className="flex items-center justify-between px-3 py-1 text-xs text-slate-400">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{c.name}</span>
                  <span>{c.category}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 px-1">
                  <button
                    onClick={() => onSelect(c.id, 'terms_of_service')}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border border-slate-200 dark:border-slate-700/70 hover:border-indigo-300 dark:hover:border-indigo-800 text-left transition-all group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-500" />
                      <div>
                        <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                          Terms of Service
                        </div>
                        <div className="text-[10px] text-slate-500">{c.domain}</div>
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-transform" />
                  </button>

                  <button
                    onClick={() => onSelect(c.id, 'privacy_policy')}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 hover:bg-teal-50 dark:hover:bg-teal-950/40 border border-slate-200 dark:border-slate-700/70 hover:border-teal-300 dark:hover:border-teal-800 text-left transition-all group"
                  >
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-teal-500" />
                      <div>
                        <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 group-hover:text-teal-600 dark:group-hover:text-teal-400">
                          Privacy Policy
                        </div>
                        <div className="text-[10px] text-slate-500">{c.domain}</div>
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const SearchModal = QuickSearchModal;
export default QuickSearchModal;
